import type {
  CreateQuotationRequest,
  QuotationResponse,
  SetQuotationStatusRequest,
  UpdateQuotationRequest,
} from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

import { INVOICES_KEY } from './api';

/** SAL-01 data layer — same shape as the invoices one. */

export const QUOTES_KEY = ['quotations'] as const;
export const ORDERS_KEY = ['sales-orders'] as const;

export interface QuoteListFilters {
  partnerId?: string | undefined;
  status?: QuotationResponse['status'] | undefined;
  /** The search box: printed number, customer, or field 13's customer reference. */
  q?: string | undefined;
  /** Wave 3B — the list's window on the quotation date, both inclusive. */
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
}

function quoteQuery(filters: QuoteListFilters) {
  return {
    ...(filters.partnerId ? { partnerId: filters.partnerId } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.q ? { q: filters.q } : {}),
    ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
    ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
  };
}

/** SAL-01's list, one page at a time (NFR-42). */
export function useQuotations(filters: QuoteListFilters, page: number, pageSize: number) {
  return useQuery({
    queryKey: [...QUOTES_KEY, filters, page, pageSize],
    queryFn: async () => {
      const response = await api.quotations.list({ query: { ...quoteQuery(filters), page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/** The export's page read — the same query the screen shows, imperative so `fetchAllPages` can walk it. */
export async function fetchQuotationsPage(
  filters: QuoteListFilters,
  page: number,
  pageSize: number,
): Promise<{ rows: QuotationResponse[]; total: number }> {
  const response = await api.quotations.list({ query: { ...quoteQuery(filters), page, pageSize } });
  if (response.status !== 200) throwApiError(response.status, response.body);
  return { rows: response.body.quotations, total: response.body.total };
}

/** Wave 3B — SAL-01's list tiles over the whole register (PAGINATION.md §3 step 10). */
export function useQuotationStats() {
  return useQuery({
    queryKey: [...QUOTES_KEY, 'stats'],
    queryFn: async () => {
      const response = await api.quotations.stats();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useQuotation(quotationId: string | undefined) {
  return useQuery({
    queryKey: [...QUOTES_KEY, 'one', quotationId],
    enabled: Boolean(quotationId),
    queryFn: async (): Promise<QuotationResponse> => {
      const response = await api.quotations.get({ params: { quotationId: quotationId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useCreateQuotation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateQuotationRequest): Promise<QuotationResponse> => {
      const response = await api.quotations.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUOTES_KEY }),
  });
}

export function useUpdateQuotation(quotationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateQuotationRequest): Promise<QuotationResponse> => {
      const response = await api.quotations.update({ params: { quotationId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUOTES_KEY }),
  });
}

export function useDeleteQuotation(quotationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const response = await api.quotations.remove({ params: { quotationId } });
      if (response.status !== 204) throwApiError(response.status, response.body);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUOTES_KEY }),
  });
}

/** W1's guarded transitions (BR-SAL-01-02) — the server refuses bad jumps. */
export function useSetQuotationStatus(quotationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SetQuotationStatusRequest): Promise<QuotationResponse> => {
      const response = await api.quotations.setStatus({ params: { quotationId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUOTES_KEY }),
  });
}

/** NEW REVISION — freezes the prior version, reopens as Draft (BR-SAL-01-10). */
export function useNewRevision(quotationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<QuotationResponse> => {
      const response = await api.quotations.newRevision({ params: { quotationId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUOTES_KEY }),
  });
}

/** Convert to Invoice — a new draft invoice appears, so that cache moves too. */
export function useConvertToInvoice(quotationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ quotation: QuotationResponse; invoiceId: string }> => {
      const response = await api.quotations.convertToInvoice({ params: { quotationId }, body: {} });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUOTES_KEY });
      void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
    },
  });
}

/**
 * Wave 4B — the same Convert to Order, from the ORDER form's "From
 * quotation…" picker: the quotation is chosen at call time, not at mount.
 */
export function useConvertPickedQuotation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (quotationId: string): Promise<{ quotation: QuotationResponse; orderId: string }> => {
      const response = await api.quotations.convertToOrder({ params: { quotationId }, body: {} });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUOTES_KEY });
      void queryClient.invalidateQueries({ queryKey: ORDERS_KEY });
    },
  });
}

/** Convert to Order — SAL-02's "From QUO-####". */
export function useConvertToOrder(quotationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ quotation: QuotationResponse; orderId: string }> => {
      const response = await api.quotations.convertToOrder({ params: { quotationId }, body: {} });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUOTES_KEY });
      void queryClient.invalidateQueries({ queryKey: ORDERS_KEY });
    },
  });
}
